/* eslint-disable no-console */
import { ActionCreator } from '@reduxjs/toolkit';
import {
  brandsChanged,
  categoryChanged,
  filtersReset,
  freeTextSearch,
  searchItemClick,
  suggestionSearch,
} from 'store/actions/search.actions';
import { StoreData } from 'store/root-reducer';
import { selectSelectedBrands, selectSelectedCategories } from 'store/selectors/filters';
import { GTM } from 'utils/analytics';

import { ActionHandler, createMiddleware } from './createMiddleware';

const getSearchQueryFromUrl = () => window.location.pathname.split('/').pop();

const getCommonParams = (state: StoreData) => ({
  query: getSearchQueryFromUrl(),
  brands: selectSelectedBrands(state),
  categories: selectSelectedCategories(state),
});

/**
 * GA `search` event on free-entry search in the search box.
 * Provides brands and categories if available.
 */
const freeTextHandler: ActionHandler<ReturnType<typeof freeTextSearch>> = (store, _next, action) => {
  const state = store.getState();

  const query = action.payload;
  const { brands, categories } = getCommonParams(state);

  GTM.search(action.type, { query, brands, categories });
};

/**
 * GA `search` event on suggestion search in the search box.
 * Provides brands and categories if available. Provides suggestion type when available.
 */
const suggestionHandler: ActionHandler<ReturnType<typeof suggestionSearch>> = (store, _next, action) => {
  const state = store.getState();

  const { query, suggestionType } = action.payload;
  const { brands, categories } = getCommonParams(state);

  GTM.search(action.type, { query, brands, categories, suggestionType });
};

/**
 * GA `search` event on brand change.
 * Provides query and categories if available.
 */
const brandsChangeHandler: ActionHandler<ReturnType<typeof brandsChanged>> = (store, _next, action) => {
  const state = store.getState();

  const brands = action.payload;
  const { query, categories } = getCommonParams(state);

  GTM.search(action.type, { query, brands, categories });
};

/**
 * GA `search` event on category change.
 * Provides query and brands if available.
 */
const categoriesChangeHandler: ActionHandler<ReturnType<typeof categoryChanged>> = (store, _next, action) => {
  const state = store.getState();

  const categories = action.payload;
  const { query, brands } = getCommonParams(state);

  GTM.search(action.type, { query, brands, categories });
};

/**
 * GA `search` event on filters reset.
 * Provides query if available.
 */
const filtersResetHandler: ActionHandler<ReturnType<typeof filtersReset>> = (_store, _next, action) => {
  GTM.search(action.type, { query: getSearchQueryFromUrl() });
};

/**
 * GA `search_item_clicked` event on search item click.
 * Provides index.
 */
const searchItemClickHandler: ActionHandler<ReturnType<typeof searchItemClick>> = (_store, _next, action) => {
  GTM.searchItemClicked(action.payload.index + 1);
};

const mapping: [ActionCreator<unknown> | string, ActionHandler<unknown>][] = [
  [freeTextSearch, freeTextHandler],
  [suggestionSearch, suggestionHandler],
  [brandsChanged, brandsChangeHandler],
  [categoryChanged, categoriesChangeHandler],
  [filtersReset, filtersResetHandler],
  [searchItemClick, searchItemClickHandler],
];

export default createMiddleware(new Map(mapping));
